import { type FC, type FormEvent, useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { useSearchParams } from 'react-router-dom';
import { bannerURL } from '../../../constants/generalData';
import {
  DEFAULT_BUSINESS_NAME,
  PRIVACY_POLICY_URL,
  TERMS_OF_SERVICE_URL,
} from './smsConsentLegal';
import {
  type SmsConsentFieldErrors,
  validateSmsConsentForm,
} from './smsConsentValidation';
import '../styles/styles.css';
import './styles/styles.css';

/** Shown under the brand name; edit copy here for Twilio campaign alignment. */
const SMS_CONSENT_CAMPAIGN_DESCRIPTION =
  'Sign up to receive SMS updates from us about your reservation, stay, and guest services.';

interface SmsConsentHeroProps {
  businessName: string;
}

const SmsConsentHero: FC<SmsConsentHeroProps> = ({ businessName }) => {
  const style = {
    backgroundImage: `linear-gradient(
      180deg,
      rgba(15, 15, 15, 0.65) 0%,
      rgba(15, 15, 15, 0.75) 38%,
      rgba(15, 15, 15, 0.8) 47%,
      rgba(15, 15, 15, 0.9) 65%,
      rgba(15, 15, 15, 1) 82%
    ), url(${bannerURL})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
  };

  return (
    <>
      <Container style={style} className="image-container" aria-hidden />
      <div className="container-paddings pt-3 pb-2">
        <div className="table-title">
          <h3 className="mb-2 mt-0">{businessName}</h3>
          <h6 className="mb-0" style={{ lineHeight: 1.5 }}>
            {SMS_CONSENT_CAMPAIGN_DESCRIPTION}
          </h6>
        </div>
      </div>
    </>
  );
};

export const SmsConsent: FC = () => {
  const [searchParams] = useSearchParams();

  const businessName = useMemo(() => {
    const fromQuery = searchParams.get('businessName')?.trim();
    return fromQuery && fromQuery.length > 0 ? fromQuery : DEFAULT_BUSINESS_NAME;
  }, [searchParams]);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');

  const [optTransactional, setOptTransactional] = useState(false);
  const [optPromotional, setOptPromotional] = useState(false);
  const [optTerms, setOptTerms] = useState(false);

  const [submitted, setSubmitted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<SmsConsentFieldErrors>({});

  useEffect(() => {
    document.title = `SMS consent — ${businessName}`;
  }, [businessName]);

  const clearFieldError = (field: keyof SmsConsentFieldErrors) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleContinue = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors = validateSmsConsentForm({
      firstName,
      lastName,
      phoneNumber,
      email,
      acceptTerms: optTerms,
    });

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }

    setFieldErrors({});
    setSubmitted(true);
  };

  return (
    <Container
      fluid
      className="page-container sms-consent-page"
      id="guest-portal"
      style={{ paddingLeft: '0px', paddingRight: '0px' }}
    >
      <Card
        className="card-item sms-consent"
        style={{ paddingLeft: '0px', paddingRight: '0px' }}
      >
        <SmsConsentHero businessName={businessName} />

        <div className="container-paddings pb-4">
          {submitted ? (
            <Alert variant="success" className="border-0 rounded-0 mt-3">
              Thank you. Your preferences have been saved.
            </Alert>
          ) : (
            <Form noValidate onSubmit={handleContinue}>
              <Row className="g-3 mt-1">
                <Col xs={12} md={6}>
                  <Form.Label
                    className="sms-consent-label"
                    htmlFor="sms-consent-first-name"
                  >
                    Name
                  </Form.Label>
                  <Form.Control
                    className="sms-consent-control"
                    id="sms-consent-first-name"
                    autoComplete="given-name"
                    isInvalid={!!fieldErrors.firstName}
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      clearFieldError('firstName');
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {fieldErrors.firstName}
                  </Form.Control.Feedback>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Label
                    className="sms-consent-label"
                    htmlFor="sms-consent-last-name"
                  >
                    Last name
                  </Form.Label>
                  <Form.Control
                    className="sms-consent-control"
                    id="sms-consent-last-name"
                    autoComplete="family-name"
                    isInvalid={!!fieldErrors.lastName}
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      clearFieldError('lastName');
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {fieldErrors.lastName}
                  </Form.Control.Feedback>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Label className="sms-consent-label" htmlFor="sms-consent-phone">
                    Phone number
                  </Form.Label>
                  <Form.Control
                    className="sms-consent-control"
                    id="sms-consent-phone"
                    type="tel"
                    autoComplete="tel"
                    inputMode="tel"
                    isInvalid={!!fieldErrors.phoneNumber}
                    value={phoneNumber}
                    onChange={(e) => {
                      setPhoneNumber(e.target.value);
                      clearFieldError('phoneNumber');
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {fieldErrors.phoneNumber}
                  </Form.Control.Feedback>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Label className="sms-consent-label" htmlFor="sms-consent-email">
                    Email
                  </Form.Label>
                  <Form.Control
                    className="sms-consent-control"
                    id="sms-consent-email"
                    type="email"
                    autoComplete="email"
                    isInvalid={!!fieldErrors.email}
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      clearFieldError('email');
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {fieldErrors.email}
                  </Form.Control.Feedback>
                </Col>
              </Row>

              <div className="mt-4 d-flex flex-column gap-3">
                <Form.Check
                  className="sms-consent-check"
                  id="sms-consent-transactional"
                  checked={optTransactional}
                  onChange={(e) => setOptTransactional(e.target.checked)}
                  label={
                    <span className="sms-consent-check-label">
                      By checking, you are allowing to receive{' '}
                      <strong>transactional / informational SMS</strong> communications
                      regarding account notifications, customer care, etc, from{' '}
                      <strong>{businessName}</strong>. Messages frequency may vary. Data
                      rates may apply, <strong>reply STOP to opt-out</strong>.
                    </span>
                  }
                />
                <Form.Check
                  className="sms-consent-check"
                  id="sms-consent-promotional"
                  checked={optPromotional}
                  onChange={(e) => setOptPromotional(e.target.checked)}
                  label={
                    <span className="sms-consent-check-label">
                      By checking, you are allowing to receive{' '}
                      <strong>promotional / marketing SMS</strong> communications from{' '}
                      <strong>{businessName}</strong>. Frequency may vary. Data rates may
                      apply, <strong>reply HELP for help or STOP to opt-out.</strong>
                    </span>
                  }
                />
                <Form.Check
                  className="sms-consent-check"
                  id="sms-consent-terms"
                  checked={optTerms}
                  isInvalid={!!fieldErrors.terms}
                  onChange={(e) => {
                    setOptTerms(e.target.checked);
                    clearFieldError('terms');
                  }}
                  label={
                    <span className="sms-consent-check-label">
                      By checking, I accept{' '}
                      <a
                        href={TERMS_OF_SERVICE_URL}
                        rel="noopener noreferrer"
                        target="_blank"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Terms of Service
                      </a>{' '}
                      &amp;{' '}
                      <a
                        href={PRIVACY_POLICY_URL}
                        rel="noopener noreferrer"
                        target="_blank"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Privacy Policy
                      </a>
                      .
                    </span>
                  }
                />
                {fieldErrors.terms ? (
                  <div className="sms-consent-terms-error">{fieldErrors.terms}</div>
                ) : null}
              </div>

              <Button
                className="sms-consent-continue mt-4"
                type="submit"
                variant="success"
              >
                Continue
              </Button>
            </Form>
          )}
        </div>
      </Card>
    </Container>
  );
};
